import { assert } from '@sama/js-core';
import {
  Point2D,
  Point3D,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../../../team-apis/otterdam/calibration-data';
import { H3Material } from '../../../materials/basic/h3-basic-material';

export interface BrownConradyProjectionUniforms {
  k: number[];
  p: number[];
  principlePoint: Point2D;
  offset: Point3D;
}

export function setBrownConradyUniforms(
  material: H3Material,
  config: {
    currentFrameCalibrationData: SingleFrameCalibrationData;
  },
) {
  const { distortion, focalLength, principlePoint } =
    config.currentFrameCalibrationData;
  assert(
    distortion.k,
    'k coefficients are required for brown conrady projection',
  );
  assert(
    distortion.p,
    'p coefficients are required for brown conrady projection',
  );
  material.setUniform('k', distortion.k.slice(0, 2));
  material.setUniform('p', distortion.p.slice(0, 2));
  material.setUniform('focalLength', focalLength);
  material.setUniform('principlePoint', principlePoint);
}

export const brownConradyProjection = `
#if defined ( USE_BROWN_CONRADY_PROJECTION )
  // Implementation based on: Lambers, M. (2018). Realistic lens distortion rendering.
  // https://dspace5.zcu.cz/bitstream/11025/34634/1/Lambers.pdf
  // We only support k1,k2,p1,p2 for now.

  vec4 getBrownConradyPosition(vec3 h3Position, mat4 h3ProjectionMatrix, mat4 h3ViewMatrix) {
    // [-1..1]
    vec4 pinhole = h3ProjectionMatrix * h3ViewMatrix * modelMatrix * vec4(h3Position, 1.0);
    vec2 undistortedNDC = pinhole.xy / pinhole.w;

    // [0..imageSize]
    vec2 undistortedPixelCoordinate = (undistortedNDC * imageSize + imageSize) / 2.0;

    // [-1..1]
    vec2 normalizedCoordinate = (undistortedPixelCoordinate - principlePoint) / focalLength;

    // Radial Distortion
    float k1 = k.x;
    float k2 = k.y;

    // Tangential Distortion
    float p1 = p.x;
    float p2 = p.y;

    float s = normalizedCoordinate.x;
    float t = normalizedCoordinate.y;

    float r2 = s * s + t * t;
    float d = 1.0 + k1 * r2 + k2 * r2 * r2;
    float u = s * d + 2.0 * p1 * s * t + p2 * (r2 + 2.0 * s * s);
    float v = t * d + p1 * (r2 + 2.0 * t * t) + 2.0 * p2 * s * t;

    vec2 distortedNormalizedCoordinate = vec2(u, v);

    vec2 distortedPixelCoordinate = distortedNormalizedCoordinate * focalLength + principlePoint;

    // [-1..1]
    vec2 finalPoint = (distortedPixelCoordinate / imageSize) * 2.0 - 1.0;
    return vec4(finalPoint * pinhole.w, pinhole.z, pinhole.w);
  }

#endif
`;
